import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { NavigationEnd, NavigationStart, Router, RouterLink, RouterOutlet } from '@angular/router';
import { Hub } from 'aws-amplify/utils';
import { BehaviorSubject, combineLatest, EMPTY, Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { QuickAddActionsComponent } from '@widgets/quick-add-actions/quick-add-actions.component';
import { TrialTasksComponent } from '@widgets/trial-tasks/trial-tasks.component';

import { InvoiceWorkflowDirective } from '@features/feature-workflow/directives/invoice-workflow.directive';
import { PurchaseOrderWorkflowDirective } from '@features/feature-workflow/directives/purchase-order-workflow.directive';
import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';
import { VendorWorkflowDirective } from '@features/feature-workflow/directives/vendor-workflow.directive';
import { NavigationMenuComponent } from '@features/navigation-menu/navigation-menu.component';
import { NewTrialDialogComponent } from '@features/new-trial-dialog/new-trial-dialog.component';
import { SearchShortcutDirective } from '@features/search-modal/directives/search-shortcut.directive';
import { SearchSectionComponent } from '@features/search-section/search-section.component';
import { StageBannerComponent } from '@features/stage-banner/stage-banner.component';
import { StageIndicatorComponent } from '@features/stage-indicator/stage-indicator.component';
import { TimelineWarningBannerComponent } from '@features/timeline-warning-banner/timeline-warning-banner.component';
import { TrialDropdownComponent } from '@features/trial-dropdown/trial-dropdown.component';

import {
  TimelineBannerStatus,
  TimelineService,
} from '@models/milestone-category/milestone/timeline.service';
import { UserTasksService } from '@models/user-tasks';

import { IconComponent } from '@shared/components/icon/icon.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import {
  CMC_EXCLUDED_URLS,
  DEFAULT_CMC_REDIRECT_ROUTE,
  ROUTING_PATH,
} from '@shared/constants/routingPath';
import { VarDirective } from '@shared/directives/var.directive';
import { AppInitService } from '@shared/services/app-init.service';
import {
  EventType,
  listTimelineMilestonesQuery,
  Trial,
  TrialWorkspaceType,
} from '@shared/services/gql.service';
import { IdleService } from '@shared/services/idle.service';
import { injectFlags, LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventTrackerService } from '@shared/store/event/event-tracker.service';
import { EventService } from '@shared/store/event/event.service';
import { FormValuesQuery } from '@shared/store/form-values/form-values.query';
import { GlobalStore } from '@shared/store/global/global.store';
import { MainQuery } from '@shared/store/main/main.query';
import { MainService } from '@shared/store/main/main.service';
import { MainStore } from '@shared/store/main/main.store';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { TrialsQuery } from '@shared/store/trials/trials.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { RequireSome } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    VarDirective,
    StageIndicatorComponent,
    TrialTasksComponent,
    VendorWorkflowDirective,
    SiteWorkflowDirective,
    InvoiceWorkflowDirective,
    PurchaseOrderWorkflowDirective,
    QuickAddActionsComponent,
    IconComponent,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    RouterLink,
    StageBannerComponent,
    TimelineWarningBannerComponent,
    TrialDropdownComponent,
    NavigationMenuComponent,
    SearchSectionComponent,
    SearchShortcutDirective,
    NgClass,
    RouterOutlet,
    AsyncPipe,
  ],
  providers: [],
  selector: 'aux-main-layout',
  styleUrls: ['./main-layout.component.scss'],
  templateUrl: './main-layout.component.html',
})
export class MainLayoutComponent implements OnDestroy, OnInit {
  globalStore = inject(GlobalStore);

  protected appInitService = inject(AppInitService);
  protected authQuery = inject(AuthQuery);
  protected mainQuery = inject(MainQuery);

  private authService = inject(AuthService);
  private eventService = inject(EventService);
  private formValuesQuery = inject(FormValuesQuery);
  private idleService = inject(IdleService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainService = inject(MainService);
  private mainStore = inject(MainStore);
  private overlayService = inject(OverlayService);
  private route = inject(Router);
  private timelineService = inject(TimelineService);
  private trackEventService = inject(EventTrackerService);
  private trialsQuery = inject(TrialsQuery);
  private userTasksService = inject(UserTasksService);
  private vendorService = inject(OrganizationService);
  private workflowService = inject(WorkflowService);

  flags = injectFlags({
    showSearchBar: 'nav_search_bar',
  });

  calculatedTimelineBanner = signal<TimelineBannerStatus>(undefined);
  isCurrentRoutePCPagesOrTimeline = signal<boolean>(false);
  isHomePageCurrent = signal(this.isHomePage(this.route.url));

  timelineWarningBannerFF = this.launchDarklyService.$select(
    (flags) => flags.timeline_warning_banner
  );

  timelineBannerStatus = computed(() => {
    return !this.isCurrentRoutePCPagesOrTimeline() || !this.timelineWarningBannerFF()
      ? undefined
      : this.calculatedTimelineBanner();
  });

  showEnvBanner$ = combineLatest([
    this.launchDarklyService.select$((flags) => !!flags.environment_indicator.messages.length),
    this.mainQuery.select('showEnvBannerWarning'),
  ]).pipe(map(([featureFlag, showEnvBannerWarning]) => featureFlag && showEnvBannerWarning));

  accountLink = `/${ROUTING_PATH.ACCOUNT.INDEX}`;
  currentRoute$ = new BehaviorSubject<string>('');
  error = false;
  lsTimelineBannerStatus$ = this.mainQuery.select('showTimelineBannerWarning');
  showEnvIndicator = this.launchDarklyService.$select(
    (flags) => !!flags.environment_indicator.messages.length
  );
  showHomeLink = this.launchDarklyService.$select((flags) => flags.nav_portfolio);
  showProfileSection = this.launchDarklyService.$select((flags) => flags.section_profile);
  showRoadmapLink = this.launchDarklyService.$select((flags) => flags.section_footer_roadmap);
  showTaskSection$ = this.launchDarklyService.select$((flags) => flags.section_task_list);
  @ViewChild(TrialDropdownComponent) trialDropdown!: TrialDropdownComponent;
  trialFc = new UntypedFormControl('');
  trials$!: Observable<
    (
      | (Omit<RequireSome<Partial<Trial>, 'id'>, 'sponsor_organization'> & {
          sponsor_organization: { id: string; name: string };
        })
      | { id: string; onboarding_complete: boolean; short_name: string }
    )[]
  >;
  unsub: () => void;

  ngOnInit(): void {
    this.trackEventService.trackEventsFromLs();
  }

  ngOnDestroy() {
    this.idleService.disableEvents.next(true);
    this.unsub();
  }

  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.unsub = Hub.listen('auth', async ({ payload }) => {
      if (
        ['signedOut', 'signInWithRedirect_failure', 'tokenRefresh_failure'].includes(payload.event)
      ) {
        await this.route.navigate([`/${ROUTING_PATH.LOGIN}`]);
        window.location.reload();
      }
    });

    this.authService
      .isAuthorized$({
        sysAdminsOnly: true,
      })
      .subscribe((hasPermission) => {
        this.trials$ = this.trialsQuery.selectAll().pipe(
          map((value) => {
            value.sort((trial) =>
              trial.workspace_type === TrialWorkspaceType.WORKSPACE_TYPE_CMC ? -1 : 1
            );

            if (hasPermission) {
              return [
                { id: 'NEW_TRIAL', onboarding_complete: true, short_name: '+ Add New Trial' },
                ...value,
              ];
            }

            return value;
          })
        );
      });
    this.idleService.disableEvents.next(false);
    this.mainQuery
      .select()
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.error = false;

        if (value.trialKey) {
          if (this.trialFc.value !== value.trialKey) {
            this.trialFc.setValue(value.trialKey);
          }
        } else {
          this.error = true;
        }
      });

    this.listenTrailEvents();

    this.handleTrailChangeSelection();

    this.trackNavigation();

    this.showTaskSection$
      .pipe(
        switchMap((show) => {
          if (show) {
            return this.userTasksService.getUserTaskList$();
          }

          return EMPTY;
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    this.fetchTrialData();
  }

  fetchTrialData() {
    this.mainQuery
      .select('trialKey')
      .pipe(
        takeUntilDestroyed(),
        filter((trialKey) => !!trialKey),
        switchMap(() => this.mainQuery.select('isTrialLoading')),
        filter((loading) => !loading),
        switchMap(() =>
          combineLatest([
            this.mainService.getOpenMonth$(),
            this.mainService.getUserList$(),
            this.timelineService.getTimelineItems().pipe(
              tap((items) => {
                this.getTimelineBannerCalculation(items).catch(console.error);
              })
            ),
            this.workflowService.getWorkflowList(),
            this.vendorService.get(),
          ])
        )
      )
      .subscribe(() => {
        // slowly migrating to global store
        this.globalStore.updateTrialId(this.mainQuery.getValue().trialKey);
      });
  }

  async getTimelineBannerCalculation(
    items: GraphqlResponse<listTimelineMilestonesQuery[]>
  ): Promise<void> {
    const currentOpenMonth = this.mainQuery.currentOpenMonth();

    if (items.data && items.success && currentOpenMonth) {
      const status = await this.timelineService.calculateTimelineBanner(
        items.data,
        currentOpenMonth
      );

      this.calculatedTimelineBanner.set(status);
    }
  }

  handleTrailChangeSelection() {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.trialFc.valueChanges.pipe(takeUntilDestroyed()).subscribe(async (value) => {
      if (this.mainQuery.getValue().trialKey !== value) {
        if (value === 'NEW_TRIAL') {
          const ref = this.overlayService.open<string>({ content: NewTrialDialogComponent });

          ref.afterClosed$.subscribe((newTrialKey) => {
            if (newTrialKey.data) {
              this.trialFc.setValue(newTrialKey.data);
            } else {
              this.trialFc.setValue(this.mainStore.getValue().trialKey);
              this.trialDropdown.setSearchTermManualy('', false);
            }
          });
        } else {
          await this.mainService.setTrial(value);
        }
      }
    });
  }

  isHomePage(url: string) {
    return url.includes(`/${ROUTING_PATH.HOME}`);
  }

  listenTrailEvents() {
    this.eventService
      .select$(EventType.REFRESH_USER_PERMISSIONS)
      .pipe(
        takeUntilDestroyed(),
        switchMap(() => this.authService.updatePermissions$())
      )
      .subscribe();

    this.eventService
      .select$(EventType.TRIAL_CHANGED)
      .pipe(
        switchMap(() => this.mainQuery.select('isTrialLoading')),
        filter((isTrialLoading) => !isTrialLoading),
        takeUntilDestroyed()
      )
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      .subscribe(async () => {
        const trial_id = await this.mainStore.getCognitoTrialKey();
        const trialChanged = this.mainQuery.getValue().trialKey !== trial_id;
        const isCurrentTrialCMC = this.trialsQuery.isCurrentTrialCMC(
          this.mainQuery.mainState().trialKey
        )();

        if (this.mainQuery.getValue().trialKey !== '' && trialChanged) {
          this.formValuesQuery.resetStore();
        }

        if (trialChanged) {
          this.trialFc.setValue(trial_id, { emitEvent: false });
          await this.mainStore.setTrial();
        }

        if (isCurrentTrialCMC && CMC_EXCLUDED_URLS.some((url) => this.route.url.includes(url))) {
          void this.route.navigateByUrl(DEFAULT_CMC_REDIRECT_ROUTE);
        }
      });

    this.eventService
      .select$(EventType.ENVIRONMENT_BUILT)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.logout().catch(console.error);
      });

    [EventType.TIMELINE_UPDATED, EventType.CLOSE_TRIAL_MONTH].forEach((event) => {
      this.eventService
        .select$(event)
        .pipe(
          takeUntilDestroyed(),
          switchMap(() =>
            combineLatest([
              this.mainService.getOpenMonth$(),
              this.timelineService.getTimelineItems(),
            ])
          )
        )
        .subscribe(([, items]) => {
          this.getTimelineBannerCalculation(items).catch(console.error);
        });
    });
  }

  async logout(manualLogOut = false) {
    if (manualLogOut) {
      localStorage.setItem(LocalStorageKey.MANUAL_LOG_OUT, 'true');
    }

    localStorage.removeItem(`customView`);
    await this.authService.signOut();
    await this.route.navigate([`/${ROUTING_PATH.LOGIN}`], { queryParams: {} });
  }

  routingHome() {
    if (this.launchDarklyService.flags$.getValue().nav_portfolio) {
      this.route.navigate([`/${ROUTING_PATH.HOME}`]).catch(console.error);
    }
  }

  shouldPagePaddingbeAdjusted(url: string) {
    return [
      ROUTING_PATH.BUDGET.INDEX,
      ROUTING_PATH.FORECAST_ROUTING.INDEX,
      ROUTING_PATH.INVESTIGATOR.INDEX,
    ].some((route) => url.includes(route));
  }

  // Periodically compares and syncs the
  // Main Store trialKey to the Cognito claim trial_id
  @HostListener('window:focus', ['$event'])
  async syncTrialKeys(): Promise<void> {
    await this.mainStore.setTrial();
  }

  trackNavigation() {
    this.route.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isHomePageCurrent.set(this.isHomePage(event.url));
      }

      if (event instanceof NavigationEnd) {
        this.currentRoute$.next(event.urlAfterRedirects);
        const currentRoute = this.currentRoute$.getValue();

        this.isCurrentRoutePCPagesOrTimeline.set(
          currentRoute.includes(`${ROUTING_PATH.CLOSING.INDEX}`) ||
            currentRoute.includes(`${ROUTING_PATH.FORECAST_ROUTING.TIMELINE}`)
        );
      }
    });
  }
}
